import {
    Box, CircularProgress, Popper, Paper, Fade, Typography,
} from '@mui/material';
import { Warning, Image } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import {
    useState, useEffect, useRef, useCallback,
} from 'react';
import debounce from 'lodash/debounce';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryDesignContent } from 'src/hooks/useQueryDesignContent';
import { useProductVersions } from 'src/hooks/useProductVersions';
import { DEFAULT_CULTURE } from 'src/constants';

interface DesignConceptPreviewProps {
    conceptId: string;
    productKey: string;
    filteringOptions?: Record<string, string>;
    defaultOptions?: Record<string, string>;
    cultures: string[];
    onStatusChange?: (status: 'idle' | 'loading' | 'success' | 'error') => void;
}

const PREVIEW_SIZE = 100;
const ENLARGED_PREVIEW_SIZE = 400;
const DEBOUNCE_DELAY = 500;

export const DesignConceptPreview = ({
    conceptId,
    productKey,
    filteringOptions,
    defaultOptions,
    cultures,
    onStatusChange,
}: DesignConceptPreviewProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery) as string;
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const previewRef = useRef<HTMLDivElement>(null);
    const [debouncedId, setDebouncedId] = useState<string>(conceptId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetId = useCallback(
        debounce((id: string) => {
            setDebouncedId(id);
        }, DEBOUNCE_DELAY),
        [],
    );

    useEffect(() => {
        debouncedSetId(conceptId);
        if (!conceptId) {
            setDebouncedId('');
            setPreviewUrl(null);
            setError(null);
        }
    }, [conceptId, debouncedSetId]);

    const handleMouseEnter = (): void => {
        if (previewRef.current && previewUrl) {
            setAnchorEl(previewRef.current);
        }
    };

    const handleMouseLeave = (): void => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const selectedOptions = { ...filteringOptions, ...defaultOptions };

    const culture = cultures.length > 0 ? cultures[0] : DEFAULT_CULTURE;

    const {
        currentProductVersion, error: versionsError,
        isLoading: isLoadingVersions,
    } = useProductVersions(productKey);

    const {
        data: designContentData,
        isLoading: isLoadingDesignContent,
        error: designContentError,
    } = useQueryDesignContent(
        accessToken,
        productKey,
        currentProductVersion || '',
        culture,
        debouncedId,
        selectedOptions,
        {
            enabled: !!accessToken && !!productKey && !!currentProductVersion && !!culture && !!debouncedId,
            onError: (err) => {
                setError(`Error fetching design content: ${err.message}`);
            },
        },
    );

    useEffect(() => {
        if (designContentData?.results?.content?.[0]) {
            const content = designContentData.results.content[0];
            const url = content.previewUrls?.size1x
                ? `https://${content.previewUrls.size1x}`
                : null;

            setPreviewUrl(url);
            setError(null);
        } else {
            setPreviewUrl(null);
            setError('Content not found');
        }
    }, [designContentData]);

    useEffect(() => {
        if (versionsError) {
            setError(`Version error: ${versionsError.message}`);
        } else if (designContentError) {
            setError(`Content error: ${designContentError.message}`);
        } else {
            setError(null);
        }
    }, [versionsError, designContentError]);

    useEffect(() => {
        if (!conceptId) {
            onStatusChange?.('idle');
            return;
        }

        if (isLoadingVersions || isLoadingDesignContent) {
            onStatusChange?.('loading');
            return;
        }

        if (error || !previewUrl) {
            onStatusChange?.('error');
            return;
        }

        onStatusChange?.('success');
    }, [
        conceptId,
        error,
        isLoadingDesignContent,
        isLoadingVersions,
        onStatusChange,
        previewUrl,
    ]);

    const isLoading = isLoadingVersions || isLoadingDesignContent;

    if (!conceptId) {
        return (
            <Box
                sx={{
                    width: PREVIEW_SIZE,
                    height: PREVIEW_SIZE,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                }}
            >
                <Image sx={{ color: 'text.disabled', fontSize: 32, mb: 1 }} />
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{
                width: PREVIEW_SIZE,
                height: PREVIEW_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
            }}
            >
                <CircularProgress size={24} />
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        animation: 'wave 1.5s infinite ease-in-out',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        '@keyframes wave': {
                            '0%': { transform: 'translateX(-100%)' },
                            '100%': { transform: 'translateX(100%)' },
                        },
                    }}
                />
            </Box>
        );
    }

    if (error || !previewUrl) {
        return (
            <Box sx={{
                width: PREVIEW_SIZE,
                height: PREVIEW_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                >
                    <Warning color="error" sx={{ fontSize: 48 }} />
                    <Typography align="center" color="text.secondary" variant="caption">
                        {error || 'Failed to load preview'}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box
                ref={previewRef}
                sx={{
                    position: 'relative',
                    cursor: 'zoom-in',
                    display: 'inline-flex',
                    width: PREVIEW_SIZE,
                    height: PREVIEW_SIZE,
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Box
                    alt={conceptId}
                    component="img"
                    src={previewUrl}
                    sx={{
                        width: PREVIEW_SIZE,
                        height: PREVIEW_SIZE,
                        objectFit: 'contain',
                        borderRadius: 1,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            boxShadow: '0 0 0 2px #2196f3',
                        },
                    }}
                />
            </Box>

            <Popper
                transition
                anchorEl={anchorEl}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 10],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            altAxis: true,
                            tether: true,
                            padding: 16,
                        },
                    },
                ]}
                open={open}
                placement="right-start"
                sx={{ zIndex: 1500 }}
            >
                {({ TransitionProps }): JSX.Element => (
                    <Fade {...TransitionProps} timeout={200}>
                        <Paper
                            elevation={8}
                            sx={{
                                p: 1,
                                borderRadius: 1,
                                maxWidth: ENLARGED_PREVIEW_SIZE + 16,
                                maxHeight: ENLARGED_PREVIEW_SIZE + 16,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Box
                                alt={conceptId}
                                component="img"
                                src={previewUrl}
                                sx={{
                                    width: ENLARGED_PREVIEW_SIZE,
                                    height: ENLARGED_PREVIEW_SIZE,
                                    objectFit: 'contain',
                                }}
                            />
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </>
    );
};
